




















































































import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { NotificationData } from '@/interfaces/notification';
import { getRouterList, RouteItem } from '@/config/routerList';

@Component
export default class NotificationDrawer extends Vue {
  visible: boolean = false;
  private refreshInterval: number | null = null;
  private readonly NOTIFICATIONS_STORAGE_KEY = 'sp_notifications';

  // Available routes from router - automatically populated from imported routerList
  get availableRoutes(): { [key: string]: string } {
    const routes: { [key: string]: string } = {};
    
    // Extract routes from imported routerList
    const routerList = getRouterList();
    this.extractRoutesFromList(routerList, routes);
    
    return routes;
  }

  // Notifications data loaded from localStorage
  notifications: NotificationData[] = [];

  showDrawer(): void {
    this.visible = true;
    // Refresh notifications data when drawer is opened
    this.loadNotificationsFromStorage();
  }

  onClose(): void {
    this.visible = false;
  }

  deleteNotification(id: string): void {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index > -1) {
      this.notifications.splice(index, 1);
      // Save the updated notifications back to localStorage
      this.saveNotificationsToStorage();
    }
  }

  private saveNotificationsToStorage(): void {
    try {
      localStorage.setItem(this.NOTIFICATIONS_STORAGE_KEY, JSON.stringify(this.notifications));
    } catch (error) {
      console.warn('Failed to save notifications to localStorage:', error);
    }
  }

  // Public method to manually refresh notifications
  public refreshNotifications(): void {
    this.loadNotificationsFromStorage();
  }

  // Public method to get notification count
  public getNotificationCount(): number {
    return this.notifications.length;
  }

  private extractRoutesFromList(routerList: RouteItem[], routes: { [key: string]: string }): void {
    routerList.forEach(route => {
      // Only include routes that have an href and are not external or hidden
      if (route.href && !route.external && !route.isHidden) {
        routes[route.id] = route.href;
      }
      
      // Handle nested routes (child routes)
      if (route.child && Array.isArray(route.child)) {
        this.extractRoutesFromList(route.child, routes);
      }
    });
  }

  private loadNotificationsFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.NOTIFICATIONS_STORAGE_KEY);
      if (stored) {
        const parsedNotifications = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        this.notifications = parsedNotifications.map((notification: any) => ({
          ...notification,
          timestamp: new Date(notification.timestamp)
        }));
      } else {
        this.notifications = [];
      }
    } catch (error) {
      console.warn('Failed to load notifications from localStorage:', error);
      this.notifications = [];
    }
  }


  getNotificationIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      'success': 'check-circle',
      'error': 'close-circle',
      'warning': 'close-circle',
      'info': 'info-circle',
      'confirmation': 'question-circle'
    };
    return iconMap[type] || 'info-circle';
  }

  getNotificationColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      'success': '#52c41a',
      'error': '#ff4d4f',
      'warning': '#ff4d4f',
      'info': '#1890ff',
      'confirmation': '#722ed1'
    };
    return colorMap[type] || '#1890ff';
  }

  formatTime(timestamp: Date): string {
    const now = moment();
    const time = moment(timestamp);
    
    const userTime = time.local();
    
    if (now.diff(userTime, 'days') === 0) {
      return this.$t('notificationPanel.timeToday') as string + ', ' + userTime.format('HH:mm');
    } else if (now.diff(userTime, 'days') === 1) {
      return this.$t('notificationPanel.timeYesterday') as string + ', ' + userTime.format('HH:mm');
    } else if (now.diff(userTime, 'days') > 1 && now.diff(userTime, 'years') < 1) {
      return userTime.format('D MMMM, HH:mm');
    } else {
      return userTime.format('D.MM.YYYY, HH:mm');
    }
  }

    // Lifecycle methods
  mounted(): void {
    // Load notifications from localStorage on component creation
    this.loadNotificationsFromStorage();
    
    // Set up interval to refresh notifications every 10 seconds
    this.refreshInterval = window.setInterval(() => {
      this.loadNotificationsFromStorage();
    }, 10000);
    
  }

  beforeDestroy(): void {
    // Clean up the refresh interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

}
